export interface SubCategoryDefinition {
  /** Sub category slug, e.g. `hate_harassment`. */
  key: string
  label: string
  /** Appended to the category subtitle when this sub category is selected. */
  value: string
}

export interface ContentWarningDefinition {
  /** Category slug, e.g. `mis_dis_information`. */
  category: string
  label: string
  title: string
  subtitle: string
  subCategories?: readonly SubCategoryDefinition[]
}

/** The sub category value the monolith uses to mean "free-form custom copy". */
export const CUSTOM_SUB_CATEGORY = 'custom'

/**
 * Taken from `https://github.com/github/github/blob/master/config/health/trust_safety/content_warnings.json`
 */
export const BANNER_CONTENT_WARNINGS = [
  {
    category: 'student_pages',
    label: 'Student pages',
    title: 'This page was made as a personal project in connection with an educational exercise.',
    subtitle:
      'This is NOT the official site of the company or brand identified on the page. The creator of this page is NOT affiliated with the company or brand in any way. DO NOT enter any personal information (such as logins, passwords or credit card numbers) on this site.',
  },
  {
    category: 'mis_dis_information',
    label: 'Mis/disinformation',
    title: 'This page may contain false or misleading information.',
    // The subtitle ends mid-sentence ("...may contain") so a sub category value or the custom copy completes it.
    subtitle:
      'The information contained in this page has not been verified and may include false, inaccurate, fabricated, or otherwise deceptive claims. In particular, it has been determined that this page may contain',
    subCategories: [
      {
        key: 'medical_scientific',
        label: 'Medical/Scientific',
        value:
          'inaccurate or scientifically unsupported medical claims or advice that have not been written by a medical professional and could endanger public health or safety.',
      },
      {
        key: 'manipulated_media',
        label: 'Manipulated Media',
        value:
          'manipulated media, whether audio or visual, likely to mislead or deceive in a way that may harm the public interest.',
      },
      {
        key: 'civic_activities',
        label: 'Civic Activities',
        value:
          "false or misleading content likely to interfere with an individual's ability to participate in civic activities.",
      },
      {
        key: 'hate_harassment',
        label: 'Hate/Harassment',
        value: 'unsubstantiated claims that could promote hate or targeted harassment of specific groups of people.',
      },
    ],
  },
] as const satisfies readonly ContentWarningDefinition[]

/**
 * Union of every content-warning category slug defined in `BANNER_CONTENT_WARNINGS`,
 * e.g. `'student_pages' | 'mis_dis_information' | ...`.
 */
export type ContentWarningCategory = (typeof BANNER_CONTENT_WARNINGS)[number]['category']

export interface ContentWarning {
  /** Content-warning category, e.g. `mis_dis_information` or `student_pages`. */
  category: ContentWarningCategory
  /** Optional sub category selected by Trust & Safety, e.g. `hate_harassment`. */
  subCategory?: string | null
  /** Optional free-form copy appended to the description by an analyst. */
  customSubCategory?: string | null
}

export interface ContentWarningPayload {
  /** The repository's content warning. */
  contentWarning: ContentWarning
  /** Repository owner login, used to build the dismiss path. */
  owner: string
  /** Repository name, used to build the dismiss path. */
  repo: string
}
